// Shared design primitives + helpers for the Brand Protection tabs.
//
// Everything in here is consumed by 10 tab components — keep it tight,
// purely presentational, and dependency-free.
import React from 'react';

// ─── Palette ────────────────────────────────────────────────────────────

export const SENTIMENT_COLOR = {
    positive: '#16a34a',
    neutral: '#94a3b8',
    negative: '#dc2626',
};

export const PLATFORM_ICON = {
    x: '𝕏', twitter: '𝕏', reddit: 'R', bluesky: '🦋', linkedin: 'in',
    facebook: 'f', instagram: 'IG', telegram: 'TG', web: '🌐',
    forums: '🗣', youtube: '▶', tiktok: '♪',
};

// Severity → border-left accent stripe.
export const SEVERITY_BORDER = {
    Critical: '#7f1d1d',
    High: '#dc2626',
    Medium: '#f97316',
    Low: '#facc15',
    Info: '#3b82f6',
};

// Compact tone palette used by Pill / Chip components.
const TONES = {
    slate: { fg: '#334155', bg: 'rgba(148,163,184,0.15)', border: 'rgba(148,163,184,0.35)' },
    green: { fg: '#15803d', bg: 'rgba(34,197,94,0.15)', border: 'rgba(34,197,94,0.4)' },
    red: { fg: '#b91c1c', bg: 'rgba(220,38,38,0.18)', border: 'rgba(220,38,38,0.55)' },
    amber: { fg: '#b45309', bg: 'rgba(249,115,22,0.18)', border: 'rgba(249,115,22,0.55)' },
    blue: { fg: '#1e40af', bg: 'rgba(59,130,246,0.15)', border: 'rgba(59,130,246,0.4)' },
    purple: { fg: '#6d28d9', bg: 'rgba(124,58,237,0.18)', border: 'rgba(124,58,237,0.5)' },
    yellow: { fg: '#854d0e', bg: 'rgba(250,204,21,0.20)', border: 'rgba(250,204,21,0.5)' },
};


// ─── Number / time helpers ──────────────────────────────────────────────

export function formatNumber(n) {
    if (n == null) return '0';
    if (n >= 1_000_000_000) return (n / 1_000_000_000).toFixed(1) + 'B';
    if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
    if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K';
    return String(n);
}

// Backend emits UTC timestamps; some endpoints omit the trailing 'Z', which
// browsers then parse as local time. Force UTC if no offset/Z is present.
export function toUtcDate(iso) {
    if (!iso) return null;
    const hasTz = /[zZ]|[+-]\d{2}:?\d{2}$/.test(iso);
    const d = new Date(hasTz ? iso : iso + 'Z');
    return isNaN(d.getTime()) ? null : d;
}

const USER_TZ = (() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone; }
    catch (_) { return undefined; }
})();

export function formatLocalDateTime(iso) {
    const d = toUtcDate(iso);
    if (!d) return iso || '';
    return d.toLocaleString(undefined, { timeZone: USER_TZ });
}

export function timeAgo(iso) {
    const d = toUtcDate(iso);
    if (!d) return iso || '';
    const s = Math.floor((Date.now() - d.getTime()) / 1000);
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    if (s < 86400 * 7) return `${Math.floor(s / 86400)}d ago`;
    // >7d: show calendar date in the viewer's local timezone (DD-MM-YYYY).
    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: USER_TZ, day: '2-digit', month: '2-digit', year: 'numeric',
    }).formatToParts(d).reduce((a, p) => (a[p.type] = p.value, a), {});
    return `${parts.day}-${parts.month}-${parts.year}`;
}


// ─── API envelope unwrap ────────────────────────────────────────────────

export function unwrap(res) {
    // ApiIntercepter reshapes axios into { statusCode, errors, headers,
    // httpStatus, ... } where statusCode is the raw JSON body. v1 wraps
    // payload under response_data.data, the BP endpoints just under data.
    const body = res?.statusCode ?? res?.data ?? res;
    return body?.response_data?.data
        ?? body?.response_data
        ?? body?.data
        ?? {};
}


// ─── Pill / chip ────────────────────────────────────────────────────────

export function Pill({ children, tone = 'slate', title, style }) {
    const p = TONES[tone] || TONES.slate;
    return (
        <span title={title} style={{
            display: 'inline-block', padding: '2px 8px', borderRadius: 4,
            fontSize: 10, fontWeight: 600, letterSpacing: 0.4,
            color: p.fg, background: p.bg, border: `1px solid ${p.border}`,
            marginRight: 6, whiteSpace: 'nowrap',
            ...style,
        }}>{children}</span>
    );
}


// ─── Skeleton (animated loading bar) ────────────────────────────────────

export function Skeleton({ width = '100%', height = 14, style }) {
    return (
        <span
            className="bm_skeleton"
            style={{
                display: 'inline-block',
                width, height,
                borderRadius: 4,
                background: 'linear-gradient(90deg, #e2e8f0 0%, #f1f5f9 50%, #e2e8f0 100%)',
                backgroundSize: '200% 100%',
                animation: 'bm_skeleton_shimmer 1.4s ease-in-out infinite',
                ...style,
            }}
            aria-hidden="true"
        />
    );
}


// ─── Section header (title + count chip + optional action) ──────────────

export function SectionHeader({ title, count, action, style }) {
    return (
        <div className="bm_section_header" style={{
            display: 'flex', alignItems: 'center', gap: 10,
            margin: '20px 0 10px 0',
            ...style,
        }}>
            <span className="bm_section_title_text" style={{
                fontSize: 13, fontWeight: 600, letterSpacing: 0.4,
                textTransform: 'uppercase', color: '#475569',
            }}>{title}</span>
            {count != null && (
                <Pill tone="blue">{count}</Pill>
            )}
            {action && (
                <span style={{ marginLeft: 'auto' }}>{action}</span>
            )}
        </div>
    );
}


// ─── Empty state ────────────────────────────────────────────────────────
//
// Variant: a small abstract bar-chart SVG mark stands in for the old
// decorative emoji. It mirrors the data-vis idiom of the rest of the
// dashboard and reads as "no data" without leaning on a character.

const EMPTY_MARK_BARS = (
    <svg width="44" height="32" viewBox="0 0 44 32" aria-hidden="true"
        style={{ opacity: 0.55 }}>
        <rect x="2" y="22" width="6" height="8" rx="1" fill="#cbd5e1" />
        <rect x="11" y="14" width="6" height="16" rx="1" fill="#cbd5e1" />
        <rect x="20" y="20" width="6" height="10" rx="1" fill="#cbd5e1" />
        <rect x="29" y="8" width="6" height="22" rx="1" fill="#cbd5e1" />
        <rect x="38" y="16" width="4" height="14" rx="1" fill="#cbd5e1" />
        <line x1="0" y1="31" x2="44" y2="31" stroke="#94a3b8" strokeWidth="0.6" />
    </svg>
);

const EMPTY_MARK_DOTS = (
    <svg width="44" height="32" viewBox="0 0 44 32" aria-hidden="true"
        style={{ opacity: 0.55 }}>
        {[6, 16, 26, 36].map((cx, i) => (
            <circle key={i} cx={cx} cy={16} r={i === 1 || i === 2 ? 4 : 3}
                fill="#cbd5e1" />
        ))}
        <line x1="0" y1="30" x2="44" y2="30" stroke="#94a3b8" strokeWidth="0.6" />
    </svg>
);

const EMPTY_MARK_LINE = (
    <svg width="60" height="32" viewBox="0 0 60 32" aria-hidden="true"
        style={{ opacity: 0.55 }}>
        <polyline points="0,22 12,18 24,24 36,12 48,16 60,8"
            fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeLinejoin="round" />
        {[
            [0, 22], [12, 18], [24, 24], [36, 12], [48, 16], [60, 8],
        ].map(([x, y], i) => (
            <circle key={i} cx={x} cy={y} r="2" fill="#cbd5e1" />
        ))}
    </svg>
);

export const EMPTY_MARKS = {
    bars: EMPTY_MARK_BARS,
    dots: EMPTY_MARK_DOTS,
    line: EMPTY_MARK_LINE,
};

export function EmptyState({ mark = 'bars', title, body, action, style }) {
    const markSvg = EMPTY_MARKS[mark] || EMPTY_MARK_BARS;
    return (
        <div className="bm_empty" style={{
            textAlign: 'center', padding: '40px 24px',
            color: '#64748b', background: '#f8fafc',
            border: '1px dashed #cbd5e1', borderRadius: 8,
            ...style,
        }}>
            <div style={{ marginBottom: 10, display: 'flex', justifyContent: 'center' }}>
                {markSvg}
            </div>
            {title && <div style={{
                fontSize: 14, fontWeight: 600, color: '#334155', marginBottom: 6,
            }}>{title}</div>}
            {body && <div style={{ fontSize: 12, color: '#64748b', maxWidth: 480, margin: '0 auto' }}>{body}</div>}
            {action && <div style={{ marginTop: 14 }}>{action}</div>}
        </div>
    );
}


// ─── Metric card (consistent across Comparison, Infographic, Topic) ─────

export function MetricCard({ label, value, delta, deltaTone, footer, style }) {
    return (
        <div className="bm_metric_card" style={{
            background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6,
            padding: '12px 14px', minWidth: 140,
            ...style,
        }}>
            <div style={{
                fontSize: 11, color: '#64748b', textTransform: 'uppercase',
                letterSpacing: 0.5, fontWeight: 600
            }}>
                {label}
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', marginTop: 4 }}>
                {value}
            </div>
            {delta != null && (
                <div style={{
                    marginTop: 4, fontSize: 11, fontWeight: 600,
                    color: deltaTone === 'up' ? '#16a34a'
                        : deltaTone === 'down' ? '#dc2626'
                            : '#64748b',
                }}>
                    {delta}
                </div>
            )}
            {footer && <div style={{ marginTop: 4, fontSize: 11, color: '#94a3b8' }}>{footer}</div>}
        </div>
    );
}


// ─── Sentiment-share bar (used by Mentions card hover + Topic cards) ────

export function SentimentBar({ positive = 0, neutral = 0, negative = 0, compact = false }) {
    const sum = positive + neutral + negative || 1;
    const pct = (n) => (n / sum) * 100;
    return (
        <div style={{
            width: '100%', height: compact ? 4 : 6, display: 'flex',
            borderRadius: 3, overflow: 'hidden', background: '#e2e8f0',
        }}>
            <span style={{ width: pct(positive) + '%', background: SENTIMENT_COLOR.positive }} />
            <span style={{ width: pct(neutral) + '%', background: SENTIMENT_COLOR.neutral }} />
            <span style={{ width: pct(negative) + '%', background: SENTIMENT_COLOR.negative }} />
        </div>
    );
}


// ─── Hashtag / mention highlighter ──────────────────────────────────────

// NOVA AI summaries emit "• …" lines joined by single newlines, which
// CommonMark collapses into one run-on paragraph. Convert bullet lines to
// real markdown list items so ReactMarkdown renders a clean list.
export function mdNormalize(text) {
    return (text || '')
        .split('\n')
        .map(line => line.replace(/^\s*•\s*/, '- '))
        .join('\n');
}

export function highlightHashtags(text) {
    if (!text) return null;
    const parts = String(text).split(/(\s+)/);
    return parts.map((part, i) => {
        if (/^#\w/.test(part)) {
            return <span key={i} style={{ color: '#3b82f6', fontWeight: 500 }}>{part}</span>;
        }
        if (/^@\w/.test(part)) {
            return <span key={i} style={{ color: '#7c3aed', fontWeight: 500 }}>{part}</span>;
        }
        return part;
    });
}
